import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import AlertMessage from './layout/AlertMessage'
import usePlan from '../hooks/usePlan.js'
import { getPlanById, getUpgradePath } from '../Plan.js'

/**
 * FeatureGate
 *
 * Wraps any component and blocks access if the user's plan doesn't include
 * the required feature. Uses the existing AlertMessage component to prompt
 * the user to upgrade.
 *
 * Props:
 *   feature      string     — feature id from FEATURE_GATES (e.g. 'hair_diagnostics')
 *   children     ReactNode  — the component/element to protect
 *   onUpgrade    function?  — called when user confirms upgrade (e.g. open PricingPage)
 *
 * Usage:
 *   <FeatureGate feature='hair_diagnostics' onUpgrade={() => goToPricing()}>
 *     <button onClick={() => setDiagOpen(true)}>View diagnostics</button>
 *   </FeatureGate>
 */
export default function FeatureGate({ feature, children, onUpgrade }) {
  const { canAccess, requiresPlan } = usePlan()
  const [showAlert, setShowAlert] = useState(false)
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  const translateText = (keyPath, replacements = {}) => {
    let value = getTranslationText(keyPath)
    if (typeof value === 'string') {
      Object.entries(replacements).forEach(([key, replacement]) => {
        value = value.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(replacement),
        )
      })
    }
    return value
  }

  // If the user has access, render children with no changes
  if (canAccess(feature)) return <>{children}</>

  // Find the minimum plan needed to unlock this feature
  const minPlan = getPlanById(requiresPlan(feature))
  const upgradeTo = getUpgradePath(requiresPlan(feature))
  const planLabel = minPlan
    ? `${minPlan.name} (${minPlan.tier})`
    : 'a higher plan'
  const upgradeLabel = upgradeTo
    ? `${upgradeTo.name} (${upgradeTo.tier})`
    : planLabel

  // Clone the child and intercept its onClick to show the alert instead
  const gatedChild = (
    <div
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setShowAlert(true)
      }}
      style={{ cursor: 'pointer', pointerEvents: 'all', position: 'relative' }}
    >
      {/* Block all interaction on the children */}
      <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {children}
      </div>
    </div>
  )

  return (
    <>
      {/* Render the child normally but intercept clicks */}
      {gatedChild}

      {/* Alert shown when blocked user tries to access the feature */}
      {showAlert && (
        <AlertMessage
          message={translateText('chat.upgradeAlert.title')}
          subtext={translateText('chat.upgradeAlert.subtext', {
            plan: upgradeLabel,
          })}
          confirmButtonText={translateText('chat.upgradeAlert.button')}
          onConfirm={() => {
            setShowAlert(false)
            onUpgrade?.()
          }}
          onCancel={() => setShowAlert(false)}
        />
      )}
    </>
  )
}
